import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";

const TOPICS = [
  "General Inquiry",
  "Book Signing",
  "Speaking / Revival Engagement",
  "Press / Media",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: TOPICS[0],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [news, setNews] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your message has been sent.");
      setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!news) {
      toast.error("Please enter your email.");
      return;
    }
    setSubscribing(true);
    try {
      await api.post("/newsletter", { email: news });
      toast.success("Welcome to the Legacy Dispatch!");
      setNews("");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-parchment dark:bg-ink"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Contact & Engagement</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                Let’s build something that lasts.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Whether you’re hosting a revival, planning a book signing, or
                inviting Charles to speak, reach out — every message is read
                personally.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <a
                data-testid="contact-email-link"
                href="mailto:charlesmckuhn99@gmail.com"
                className="mt-8 inline-flex items-center gap-3 text-foreground/90 hover:text-gold-deep dark:hover:text-gold-light transition-colors"
              >
                <span className="p-3 rounded-full bg-card border border-border">
                  <Mail size={18} />
                </span>
                charlesmckuhn99@gmail.com
              </a>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10 p-7 rounded-2xl bg-ink text-parchment dark:bg-card dark:text-foreground border border-border">
                <h3 className="font-serif text-xl font-semibold">
                  Join the Legacy Dispatch
                </h3>
                <p className="mt-2 text-sm text-parchment/70 dark:text-muted-foreground">
                  Occasional notes on new books, revivals, and reflections.
                </p>
                <form onSubmit={subscribe} className="mt-4 flex gap-2">
                  <input
                    data-testid="newsletter-email-input"
                    type="email"
                    value={news}
                    onChange={(e) => setNews(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 px-4 py-2.5 rounded-full bg-parchment/10 dark:bg-background border border-white/15 dark:border-border text-sm focus:outline-none focus:ring-2 focus:ring-gold placeholder:text-parchment/40 dark:placeholder:text-muted-foreground"
                  />
                  <button
                    data-testid="newsletter-subscribe-button"
                    type="submit"
                    disabled={subscribing}
                    className="px-5 py-2.5 rounded-full bg-gold text-ink text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60"
                  >
                    {subscribing ? <Loader2 size={16} className="animate-spin" /> : "Join"}
                  </button>
                </form>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="p-7 sm:p-9 rounded-3xl bg-card border border-border"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-medium mb-2">Topic</label>
                  <select
                    data-testid="contact-topic-select"
                    value={form.topic}
                    onChange={update("topic")}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-gold"
                  >
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    rows={6}
                    placeholder="How can Charles help?"
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                  />
                </div>

                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold hover:bg-gold hover:text-ink dark:hover:bg-gold-light transition-colors disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send size={18} /> Send Message
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
